import React, { type ReactElement, useEffect, useState } from 'react'
import { Skeleton, Typography, useTheme } from '@mui/material'
import { formatDistanceToNow } from 'date-fns'
import srLatn from 'date-fns/locale/sr-Latn'
import { BUTTON_SIZE, BUTTON_VARIANTS } from 'common/ui/theme-button/ThemeButton'
import { StoreStatus } from 'common/utils/types'
import ThemeButton from 'common/ui/theme-button'
import SimpleTooltip from 'common/ui/simple-tooltip'
import tendersBackground from 'assets/tendersBackground.svg'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { useNavigate } from 'react-router-dom'

import { getBidList } from '../bid.service'
import { type Bid } from '../bid.types'

import styles from './AcceptedBids.module.scss'

const BIDS_PER_PAGE = 9

const AcceptedBids: React.FC = (): ReactElement => {
  const dispatch = useAppDispatch()
  const { palette } = useTheme()
  const { bidList, count, getBidListStatus } = useAppSelector((state) => state.bid)
  const [currentPage, setCurrentPage] = useState<number>(0)
  const [loadMore, setLoadMore] = useState<boolean>(false)
  const navigate = useNavigate()

  const fetchBids = (page: number): void => {
    const payload = {
      accepted: true,
      ipp: BIDS_PER_PAGE,
      page,
    }

    void dispatch(getBidList(payload))
  }

  useEffect(() => {
    fetchBids(currentPage)
  }, [])

  const loadMoreItems = (): void => {
    if (bidList.length < count) {
      const nextPage = currentPage + 1
      fetchBids(nextPage)
      setCurrentPage(nextPage)
    }
  }

  const handleScroll = (): void => {
    const distanceToBottom = document.documentElement.offsetHeight - (window.innerHeight + window.scrollY)
    if (getBidListStatus !== StoreStatus.Loading && distanceToBottom < 100) {
      setLoadMore(true)
    }
  }

  useEffect(() => {
    if (loadMore && bidList.length < count) {
      loadMoreItems()
    }
  }, [loadMore, bidList, count])

  useEffect(() => {
    window.addEventListener('scroll', handleScroll)
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  const handleDetailsClick = (bid: Bid): void => {
    navigate(`/tenderi/${bid?.id}`)
  }

  return (
    <div
      id="bidListWrapper"
      className={styles.bidListWrapper}
      style={{
        backgroundImage: `url(${tendersBackground})`,
        backgroundPosition: 'right bottom',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {getBidListStatus === StoreStatus.Loading ? (
        Array(BIDS_PER_PAGE)
          .fill(0)
          .map((value, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              variant="rounded"
              width={'100%'}
              height={96}
              sx={{ backgroundColor: palette.blue700.main, marginRight: '1rem' }}
            />
          ))
      ) : bidList.length ? (
        bidList?.map((b) => (
          <div key={b.id} className={styles.bid}>
            <div className={styles.nameWrapper}>
              <SimpleTooltip title={b.name ?? '-'} style={{ textAlign: 'left' }}>
                <Typography className={styles.truncate}>{b.name}</Typography>
              </SimpleTooltip>
            </div>
            <div className={styles.divider} />
            <div className={styles.tenderInfoWrapper}>
              <div className={styles.info}>
                <Typography variant="title2">ROK ZA SLANJE PONUDA</Typography>
                <Typography variant="title5" sx={{ color: palette.gray.main }}>
                  Još {formatDistanceToNow(new Date(b.deadlineProposal), { locale: srLatn })}
                </Typography>
              </div>
              <div className={styles.info}>
                <Typography variant="title2">BR. TENDERA</Typography>
                <Typography variant="title5" sx={{ color: palette.gray.main }}>
                  {b.tenderNum}
                </Typography>
              </div>
            </div>
            <div className={styles.divider} />
            <div className={styles.buttonWrapper}>
              <ThemeButton
                variant={BUTTON_VARIANTS.secondary}
                size={BUTTON_SIZE.small}
                onClick={() => {
                  handleDetailsClick(b)
                }}
              >
                DETALJI
              </ThemeButton>
            </div>
          </div>
        ))
      ) : (
        <div className={styles.noResults}>Trenutno nema prihvaćenih tendera</div>
      )}
    </div>
  )
}

export default AcceptedBids
